import { FigmaTypography } from "@libs/zapi-ui/custom";
import { ClaimItem } from "./ClaimItem";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { ClaimsActiveHeader } from "./_ClaimsActiveHeader";
import { LoadingWidget } from "./_LoadingWidget";
import { TClaimTab } from "../model/types";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { EvidenceItem } from "./EvidenceItem";
import { EvidenceHeader } from "./_EvidenceHeader";
import { ToggleReviewed } from "./_ToggleReviewed";
import { useClaims } from "../model/useClaims";
import { AnalyzeClaims, CancelClaimsAnalysis } from "@/zapi-frontend/features/AnalyzeClaims";
import { NoClaimsWidget } from "./_NoClaimsWidget";
import { GeneratingWidget } from "./_GeneratingWidget";

export function Claims({ projectId }: { projectId: string }) {
  const {
    currentClaim,
    isGenerating,
    progressMessage,
    filteredClaims,
    lastAnalyzedDate,
    isLoading,
    selectedTab,
    isNoClaimsYet,
    viewMode,
    isAllowedToRegenerate,
    handleReviewToggle,
    changeViewMode,
    changeTab,
    selectedClaimIndex,
    changeClaimIndex,
    onActiveChapterAnalysisComplete,
    chapterId,
    isChapterContentEmpty,
    navigateToAvailableCard
  } = useClaims({ projectId });

  const isCurrentClaimReviewed = currentClaim?.isReviewedButFrozen ?? currentClaim?.isReviewed ?? false;

  if (isLoading) return <LoadingWidget />;

  if (isNoClaimsYet) {
    return (
      <NoClaimsWidget
        analyzeSlot={
          <AnalyzeClaims
            projectId={projectId}
            onComplete={onActiveChapterAnalysisComplete}
            chapterId={chapterId}
            isChapterContentEmpty={isChapterContentEmpty}
          >
            Analyze Claims
          </AnalyzeClaims>
        }
      />
    );
  }

  if (isGenerating) {
    return (
      <GeneratingWidget cancelSlot={<CancelClaimsAnalysis chapterId={chapterId} />} progressMessage={progressMessage} />
    );
  }

  if (!selectedTab) return null;

  const isCurrentClaimFirst = selectedClaimIndex === 0;
  const isCurrentClaimLast = selectedClaimIndex === (filteredClaims?.[selectedTab] ?? [])?.length - 1;
  const counts = Object.fromEntries(
    Object.entries(filteredClaims || {}).map(([key, claims]) => [key, claims.length])
  ) as Record<TClaimTab, number>;

  if (viewMode === "evidence") {
    return (
      <div className="h-full flex flex-col flex-grow">
        <EvidenceHeader
          onChangeViewMode={changeViewMode}
          reviewedSlot={
            <ToggleReviewed isReviewed={isCurrentClaimReviewed} onChange={() => handleReviewToggle(null)} />
          }
        />
        <div className="h-full p-1 bg-innoveraZinc-100 rounded-b-[8px]">
          <div className={cn("flex flex-col gap-2 overflow-y-auto h-[calc(100%-32px)]")}>
            {currentClaim?.evidence?.map((evidence) => {
              return <EvidenceItem key={evidence.sourceId} evidence={evidence} />;
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col flex-grow">
      <ClaimsActiveHeader
        counts={counts}
        lastAnalyzedDate={lastAnalyzedDate}
        selectedTab={selectedTab}
        onTabChange={changeTab}
        reanalyzeSlot={
          <AnalyzeClaims
            projectId={projectId}
            isWithWarning={!isNoClaimsYet}
            onComplete={onActiveChapterAnalysisComplete}
            isDisabled={!isAllowedToRegenerate}
            chapterId={chapterId}
            isChapterContentEmpty={isChapterContentEmpty}
          >
            Re-analyze
          </AnalyzeClaims>
        }
      />
      <div className="flex flex-col p-1 bg-innoveraZinc-100 rounded-b-[8px] overflow-y-auto flex-1 min-h-0">
        {currentClaim && (
          <ClaimItem
            claim={currentClaim}
            onReviewToggle={handleReviewToggle}
            projectId={projectId}
            onGoNext={navigateToAvailableCard}
          />
        )}
      </div>

      <div className="mt-auto bg-white flex items-center justify-between p-3">
        <div className="flex flex-col items-start gap-2">
          <FigmaTypography variant="label-3" color="zinc500" className="text-center">
            ({selectedClaimIndex + 1} of {filteredClaims?.[selectedTab]?.length ?? 0})
          </FigmaTypography>
        </div>
        <div className="flex items-center justify-center gap-1">
          <div
            className={cn(
              "cursor-pointer rounded-[6px] border border-innoveraZinc-150 px-2 py-1",
              isCurrentClaimFirst && "pointer-events-none"
            )}
            onClick={() => changeClaimIndex("prev")}
          >
            <CaretLeftIcon
              className={cn("w-4 h-4 text-innoveraZinc-900", isCurrentClaimFirst && "text-innoveraZinc-400")}
            />
          </div>
          <div
            className={cn(
              "cursor-pointer rounded-[6px] border border-innoveraZinc-150 px-2 py-1",
              isCurrentClaimLast && "pointer-events-none"
            )}
            onClick={() => changeClaimIndex("next")}
          >
            <CaretRightIcon
              className={cn("w-4 h-4 text-innoveraZinc-900", isCurrentClaimLast && "text-innoveraZinc-400")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
